// hooks/useNotificationWebSocket.js
import { useEffect, useRef, useState, useCallback } from 'react';

const getWsUrl = () => {
  const hostname = window.location.hostname;
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'ws://localhost:8080';
  } else if (hostname === '192.168.0.175') {
    return 'ws://192.168.0.175:8080';
  } else {
    return 'wss://api.ecomart.ng/ws';
  }
};

const WS_URL = getWsUrl();

export const useNotificationWebSocket = () => {
  const wsRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const notificationHandlers = useRef(new Map());
  const reconnectTimeoutRef = useRef(null);
  const isConnectingRef = useRef(false);

  const handleIncomingMessage = useCallback((data) => {
    console.log('📨 [Notification] Received:', data.type, data);
    
    switch (data.type) {
        case 'new_notification':
            const newHandler = notificationHandlers.current.get('new');
            if (newHandler) {
                newHandler(data.notification, data.unread_count);
            }
            break;
            
        case 'notification_read':
            const readHandler = notificationHandlers.current.get('read');
            if (readHandler) {
                readHandler(data.notification_id, data.unread_count);
            }
            break;
            
        case 'notifications_cleared':
            const clearedHandler = notificationHandlers.current.get('cleared');
            if (clearedHandler) {
                clearedHandler(data.unread_count);
            }
            break;
            
        case 'unread_count_update':
            const countHandler = notificationHandlers.current.get('count');
            if (countHandler) {
                countHandler(data.unread_count);
            }
            break;
            
        case 'connected':
            console.log('✅ Connected to notification server');
            break;
            
        case 'subscribed':
            console.log('✅ Subscribed to notifications');
            break;
            
        case 'pong':
            break;
            
        default:
            console.log('Unknown notification type:', data.type);
    }
}, []);

  const connect = useCallback(() => {
    if (isConnectingRef.current) {
      console.log('⚠️ Already connecting, skipping...');
      return;
    }
    
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('No JWT token found');
      return;
    }
    
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    isConnectingRef.current = true;
    
    const wsUrl = `${WS_URL}?token=${encodeURIComponent(token)}`;
    console.log('🔌 [Notification] Connecting to:', wsUrl);
    
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    
    ws.onopen = () => {
      console.log('✅ [Notification] WebSocket connected');
      setIsConnected(true);
      isConnectingRef.current = false;
      
      // Subscribe to notifications
      ws.send(JSON.stringify({
        type: 'subscribe_notifications'
      }));
      
      // Send ping
      setTimeout(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 500);
    };
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        handleIncomingMessage(data);
      } catch (error) {
        console.error('Failed to parse message:', error);
      }
    };
    
    ws.onclose = (event) => {
      console.log('🔌 [Notification] WebSocket disconnected:', event.code);
      setIsConnected(false);
      isConnectingRef.current = false;
      
      if (event.code !== 1000 && event.code !== 1001) {
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('🔄 [Notification] Attempting to reconnect...');
          connect();
        }, 5000);
      }
    };
    
    ws.onerror = (error) => {
      console.error('[Notification] WebSocket error:', error);
      isConnectingRef.current = false;
    };
  }, [handleIncomingMessage]);

  const subscribeToNotifications = useCallback((onNewNotification, onReadUpdate, onCountUpdate) => {
    if (onNewNotification) {
      notificationHandlers.current.set('new', onNewNotification);
    }
    if (onReadUpdate) {
      notificationHandlers.current.set('read', onReadUpdate);
    }
    if (onCountUpdate) {
      notificationHandlers.current.set('count', onCountUpdate);
    }
    
    return () => {
      notificationHandlers.current.delete('new');
      notificationHandlers.current.delete('read');
      notificationHandlers.current.delete('count');
    };
  }, []);

  const markAsReadViaWebSocket = useCallback((notificationId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'mark_notification_read',
        notification_id: notificationId
      }));
    }
  }, []);

  const markAllAsReadViaWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'mark_all_read'
      }));
    }
  }, []);

  useEffect(() => {
    connect();
    
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 25000);
    
    return () => {
      clearInterval(pingInterval);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
      }
      notificationHandlers.current.clear();
      isConnectingRef.current = false;
    };
  }, [connect]);

  return {
    isConnected,
    subscribeToNotifications,
    markAsReadViaWebSocket,
    markAllAsReadViaWebSocket,
    reconnect: connect
  };
};